import type { ButtonProps } from '@mui/material/Button'
import { headingFont } from './tokens'

const pointerHover = '@media (hover: hover) and (pointer: fine)'
const blue = {
  ink: '#163f57',
  top: '#65e0f8',
  fill: '#53d9f5',
  edge: '#6adcf2',
  shadow: '#319fbb',
  shine: '#cef6ff',
  outline: '#d6e4ef',
  softShadow: '#bfd4e3',
  hover: '#f1f8fc',
  disabled: '#e2e9ef',
  disabledInk: '#728594',
  disabledEdge: '#d5dfe6',
  disabledShadow: '#cbd6df',
}
const orange = {
  ink: '#693413',
  top: '#ffbd8d',
  fill: '#ffac78',
  edge: '#f5a36b',
  shadow: '#d78750',
  shine: '#ffdfc2',
  outline: '#efddc9',
  softShadow: '#e8bb99',
  hover: '#fff6ed',
  disabled: '#f4d2b5',
  disabledInk: '#987457',
  disabledEdge: '#dfb58f',
  disabledShadow: '#d9ac87',
}
type Palette = typeof blue

// Inherited by default/primary buttons in poll views, without overriding their variant.
export const pollActionVariables = Object.fromEntries(
  Object.entries(orange).map(([key, value]) => [`--rt-action-${key}`, value]),
)

const semanticPalette = (color: ButtonProps['color']): Palette => {
  if (color === 'secondary') return orange
  if (color === 'error')
    return {
      ...blue,
      ink: '#fff',
      top: '#d75965',
      fill: '#cc4c58',
      edge: '#cc4c58',
      shadow: '#a73743',
      shine: '#ea99a0',
      outline: '#f1c2c6',
      softShadow: '#e5b0b7',
      hover: '#fff0f1',
      disabled: '#f5dce0',
      disabledInk: '#96666d',
      disabledEdge: '#e8c3ca',
      disabledShadow: '#ddb4bd',
    }
  if (color === 'success')
    return {
      ...blue,
      ink: '#fff',
      top: '#29917b',
      fill: '#21846e',
      edge: '#21846e',
      shadow: '#156451',
      shine: '#76b7a6',
      outline: '#b7ded2',
      softShadow: '#9ccbbb',
      hover: '#effaf5',
      disabled: '#dbece5',
      disabledInk: '#69867a',
      disabledEdge: '#c2dcd1',
      disabledShadow: '#b4cfc2',
    }
  if (color === 'warning')
    return {
      ...orange,
      ink: '#735119',
      top: '#ffe994',
      fill: '#ffe178',
      edge: '#efd06b',
      shadow: '#cba33a',
      shine: '#fff2ba',
      outline: '#eddaa5',
      softShadow: '#e1c782',
      hover: '#fff9e8',
    }
  return blue
}

export const buttonBase = {
  borderRadius: '18px',
  minHeight: 44,
  padding: '10px 18px',
  fontFamily: headingFont,
  fontWeight: 800,
  lineHeight: 1.2,
  '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root':
    { color: 'inherit' },
}

export function buttonAppearance(
  variant: ButtonProps['variant'] = 'text',
  color: ButtonProps['color'] = 'primary',
) {
  const palette = semanticPalette(color)
  const contextual = color === 'primary'
  const value = (key: keyof Palette) =>
    contextual ? `var(--rt-action-${key}, ${palette[key]})` : palette[key]
  const ink =
    color === 'error'
      ? '#a73743'
      : color === 'success'
        ? '#156451'
        : color === 'inherit'
          ? 'inherit'
          : value('ink')
  const disabled = {
    background: variant === 'text' ? 'transparent' : value('disabled'),
    color: value('disabledInk'),
    borderColor: variant === 'text' ? 'transparent' : value('disabledEdge'),
    boxShadow:
      variant === 'text' ? 'none' : `0 2px 0 ${value('disabledShadow')}`,
    transform: 'none',
    '& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
  }
  if (variant === 'contained') {
    const background = `linear-gradient(180deg, ${value('top')}, ${value('fill')})`
    const shadow = `0 4px 0 ${value('shadow')}, inset 0 2px 0 ${value('shine')}`
    return {
      background,
      color: value('ink'),
      border: `1px solid ${value('edge')}`,
      boxShadow: shadow,
      '&:hover': { background, boxShadow: shadow },
      [pointerHover]: { '&:hover': { transform: 'translateY(-1px)' } },
      '&:active': {
        transform: 'translateY(2px)',
        boxShadow: `0 2px 0 ${value('shadow')}`,
      },
      '&.Mui-disabled': disabled,
    }
  }
  if (variant === 'outlined') {
    const shadow = `0 3px 0 ${value('softShadow')}`
    return {
      background: '#ffffff',
      color: ink,
      border: `1px solid ${value('outline')}`,
      boxShadow: shadow,
      '&:hover': {
        background: '#ffffff',
        borderColor: value('outline'),
        boxShadow: shadow,
      },
      [pointerHover]: {
        '&:hover': {
          background: value('hover'),
          transform: 'translateY(-1px)',
        },
      },
      '&:active': {
        transform: 'translateY(1px)',
        boxShadow: `0 2px 0 ${value('softShadow')}`,
      },
      '&.Mui-disabled': disabled,
    }
  }
  return {
    background: 'transparent',
    color: ink,
    border: '1px solid transparent',
    boxShadow: 'none',
    '&:hover': { background: 'transparent', boxShadow: 'none' },
    [pointerHover]: { '&:hover': { background: value('hover') } },
    '&:active': {
      background: value('hover'),
      transform: 'none',
      boxShadow: 'none',
    },
    '&.Mui-disabled': disabled,
  }
}
